export type CalendarEventStatus = 'confirmed' | 'tentative' | 'cancelled' | 'completed';

export type CalendarEventCategory = 'primary' | 'indigo' | 'jade' | 'amber' | 'ruby' | 'violet' | 'neutral';

export type CalendarView = 'month' | 'week' | 'list';

export interface CalendarAttendee {
  name?: string;
  email?: string;
  avatarUrl?: string;
  role?: 'organizer' | 'attendee';
  status?: 'pending' | 'accepted' | 'declined';
}

export interface CalendarLocation {
  name?: string;
  address?: string;
  url?: string;
  type?: 'physical' | 'virtual' | 'phone';
  /** Indique si l'adresse exacte est révélée (règle 24h ETXE pour les agents) */
  isRevealed?: boolean;
  /** Message d'information si l'adresse est masquée */
  lockMessage?: string;
}

/**
 * Informations de synchronisation avec des standards externes (RFC 5545, Google Calendar, CalDAV)
 */
export interface CalendarSyncInfo {
  /** UID unique au format RFC 5545 (ex: "urn:uuid:..." ou "appt-123@etxe.immo") */
  uid: string;
  /** Numéro de révision/séquence RFC 5545 */
  sequence?: number;
  /** Règle de récurrence RFC 5545 (RRULE) */
  recurrenceRule?: string;
  /** Identifiant distant Google Calendar */
  googleEventId?: string;
  /** ETag / CTag CalDAV pour gestion des conflits et synchronisation bidirectionnelle */
  calDavEtag?: string;
  /** URL de la ressource CalDAV */
  calDavUrl?: string;
}

export interface CalendarEventContextItemAttribute {
  label: string;
  value: string;
  icon?: string;
}

export interface CalendarEventContextItem {
  id: string;
  type: 'property' | 'discussion' | string;
  title: string;
  subtitle?: string;
  badge?: string;
  icon?: string;
  imageUrl?: string;
  url?: string;
  route?: string | string[];
  attributes?: CalendarEventContextItemAttribute[];
}

export interface CalendarSource {
  id: string;
  name: string;
  category: CalendarEventCategory;
  visible: boolean;
  isSystem?: boolean;
  readonly?: boolean;
  badgeCount?: number;
}

/**
 * Modèle générique standard d'événement de calendrier / agenda.
 * Compatible avec les spécifications iCalendar (RFC 5545), Google Calendar et CalDAV.
 */
export interface CalendarEvent<TMetadata = unknown> {
  id: string;
  title: string;
  description?: string;
  /** Début de l'événement au format ISO 8601 */
  startAt: string;
  /** Fin de l'événement au format ISO 8601 */
  endAt: string;
  /** Indique un événement sur toute la journée */
  allDay?: boolean;
  status?: CalendarEventStatus;
  category?: CalendarEventCategory;
  location?: CalendarLocation;
  attendees?: CalendarAttendee[];
  contexts?: CalendarEventContextItem[];
  url?: string;
  /** Identifiant de la source / couche d'agenda rattachée */
  sourceId?: string;
  metadata?: TMetadata;
  syncInfo?: CalendarSyncInfo;
}

export interface CalendarSettings {
  /** Afficher ou masquer les week-ends (samedi et dimanche) */
  showWeekends: boolean;
  /** Premier jour de la semaine : 1 = Lundi, 0 = Dimanche */
  firstDayOfWeek: 1 | 0;
  /** En mode semaine, heure de début affichée (ex: 8 pour 08:00) */
  weekStartHour: number;
  /** En mode semaine, heure de fin affichée (ex: 20 pour 20:00) */
  weekEndHour: number;
}

export const DEFAULT_CALENDAR_SETTINGS: CalendarSettings = {
  showWeekends: true,
  firstDayOfWeek: 1,
  weekStartHour: 6,
  weekEndHour: 22,
};
